<script setup lang="ts">
/**
 * **信息弹窗**（「系统 → 帮助 / 关于」用）。
 *
 * ## 为什么要有这个组件，而不是继续用提示条
 *
 * 这两个菜单项一开始发的是 `showNotice`（底部那条提示）。它的问题是
 * **内容放不下**：帮助要列七八条快捷键，提示条是单行 + 横向滚动的，
 * 用户得横着划才能看全；而且提示条几秒就淡出，想照着敲第二个键时已经没了。
 *
 * 所以：**一次性的反馈**走提示条（「已断开，撤销？」），
 * **要读、要照着做**的内容走弹窗。
 *
 * ## 三条必须做的事
 *
 *   1. `Esc` 关闭 —— 弹窗盖住整个界面，没有键盘出口会让人以为卡住了；
 *   2. 点**遮罩**关闭（`@pointerdown.self`，不是 click）——
 *      用 `self` 保证只有点在弹窗**外面**才算，用 `pointerdown`
 *      是为了不和"在弹窗里拖选文字然后手滑松到外面"混起来；
 *   3. 打开时把焦点移到关闭按钮 —— 否则焦点还留在菜单按钮上，
 *      键盘用户按 Tab 会跑到弹窗**背后**的界面里去。
 *
 * ⚠️ 不引第三方弹窗库：这里只有"标题 + 内容 + 关闭"三件事，
 * 而焦点管理 / 遮罩 / 层级这套东西一旦引进库，
 * 它的样式和令牌体系还得再对齐一遍（`NpNodePicker` 已经验证过
 * 手写这一套的成本很低）。
 */
import { nextTick, onScopeDispose, ref, watch } from 'vue'

const props = defineProps<{
  open: boolean
  title: string
  /** 标题下面那行小字，一句话说清这个弹窗是干什么的 */
  hint?: string
}>()

const emit = defineEmits<{ close: [] }>()

const closeEl = ref<HTMLButtonElement | null>(null)

/**
 * ⚠️ `Esc` **挂在 document 上，不是挂在弹窗元素上**。
 *
 * 第一版写的是 `@keydown` 绑在弹窗的根元素上，靠冒泡接住按键 ——
 * 那只有在**焦点已经在弹窗里**时才成立。而弹窗刚打开的那一帧、
 * 或者用户点了内容区里一段没有焦点的文字之后，焦点可能在 `body` 上，
 * 按键**根本不会经过弹窗**，于是 Esc 没反应 —— 而弹窗盖住了整个界面，
 * 用户会以为卡住了。
 *
 * 挂 document 就没有这个问题：只要它开着，Esc 一定生效。
 *
 * ⚠️ 顺手 `stopPropagation`：全局快捷键（Delete 删节点、Ctrl+Z 撤销…）
 * 也挂在 document 上，不拦的话在弹窗里按 Delete 会把画布上选中的节点删掉。
 */
function onDocKeydown(ev: KeyboardEvent): void {
  if (ev.key !== 'Escape') return
  ev.stopPropagation()
  emit('close')
}

watch(
  () => props.open,
  async (open) => {
    if (open) {
      document.addEventListener('keydown', onDocKeydown, true)
      await nextTick()
      closeEl.value?.focus()
    } else {
      document.removeEventListener('keydown', onDocKeydown, true)
    }
  },
  { immediate: true },
)

/* 组件被卸载时也要摘掉 —— 否则监听器留在 document 上，越积越多 */
onScopeDispose(() => document.removeEventListener('keydown', onDocKeydown, true))
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="np-dialog"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
      @pointerdown.self="emit('close')"
    >
      <div class="np-dialog__sheet">
        <header class="np-dialog__head">
          <div class="np-dialog__heading">
            <h2 class="np-dialog__title">{{ title }}</h2>
            <p v-if="hint" class="np-dialog__hint">{{ hint }}</p>
          </div>
          <button
            ref="closeEl"
            class="np-dialog__close"
            type="button"
            aria-label="关闭"
            title="关闭（Esc）"
            @click="emit('close')"
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </header>
        <div class="np-dialog__body">
          <slot />
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.np-dialog {
  position: fixed;
  inset: 0;
  z-index: var(--np-z-popup);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: var(--np-scrim);
}

.np-dialog__sheet {
  display: flex;
  flex-direction: column;
  width: min(520px, 100%);
  max-height: min(78vh, 640px);
  border: 1px solid var(--np-popup-border);
  border-radius: var(--np-popup-radius);
  background: var(--np-popup-bg);
  box-shadow: var(--np-popup-shadow);
  color: var(--np-panel-text);
  overflow: hidden;
}

.np-dialog__head {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex: 0 0 auto;
  padding: 12px 12px 10px 16px;
  border-bottom: 1px solid var(--np-border-bar);
}

.np-dialog__heading {
  flex: 1 1 auto;
  min-width: 0;
}

.np-dialog__title {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
}

.np-dialog__hint {
  margin: 3px 0 0;
  color: var(--np-panel-mute);
  font-size: 11px;
}

.np-dialog__close {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: var(--np-ctl-h-md);
  height: var(--np-ctl-h-md);
  padding: 0;
  border: 0;
  border-radius: var(--np-radius-s);
  background: transparent;
  color: var(--np-panel-dim);
  cursor: pointer;
}

.np-dialog__close:hover,
.np-dialog__close:focus-visible {
  background: var(--np-surface-3);
  color: var(--np-panel-text);
}

/* 内容自己滚，标题栏钉住 —— 帮助那份内容比弹窗高 */
.np-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  padding: 12px 16px 16px;
  overflow-y: auto;
  overscroll-behavior: contain;
  font-size: 11.5px;
  line-height: 1.7;
}
</style>
